import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import IconButton from '~/components/IconButton';
import api from '~/services/api';

/**
 * Devolução pelos Correios (logística reversa — postagem em agência).
 *
 * - useCorreiosReverse: estado, polling do rótulo e ações (uma instância por tela)
 * - CorreiosReverseModal: confirmação da devolução e acompanhamento da etiqueta
 * - CorreiosReverseInfoCard: card de acompanhamento (apenas apresentação)
 */

const POLL_INTERVAL_MS = 8000;
const MAX_LABEL_POLLS = 45; // 45 x 8s = ~6 minutos

const CANCELED = 'CANCELED';
const SEARCHING = 'SEARCHING';

const STATUS_LABELS = {
  SEARCHING: 'Aguardando postagem pelo cliente',
  ON_DELIVERY: 'Em trânsito para a loja',
  PENDING_CLIENT: 'Aguardando retirada na agência',
  DELIVERED: 'Recebido pela loja',
  COMPLETED: 'Recebido pela loja',
  RETURNED: 'Devolvido ao cliente',
  CANCELED: 'Cancelada',
};

const getErrorMessage = (err, fallback) => {
  const body = err?.response?.data;

  if (body?.error && body?.detail) return `${body.error} ${body.detail}`;

  return body?.error || body?.message || fallback;
};

const formatDate = value => {
  if (!value) return '-';

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleDateString('pt-BR');
};

const formatDateTime = value => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString(
    'pt-BR',
    { hour: '2-digit', minute: '2-digit' }
  )}`;
};

export const isCorreiosReverseActive = data =>
  !!data && data.status !== CANCELED;

export function useCorreiosReverse(sale, onChanged) {
  const saleId = sale?.id;
  const orderNumber = sale?.order_number;
  const hasReverse =
    sale?.return_delivery_method === 'CORREIOS' && !!sale?.return_delivery;
  const returnDeliveryId = sale?.return_delivery?.id;
  const returnDeliveryStatus = sale?.return_delivery?.status;

  const [data, setData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const mountedRef = useRef(true);
  const pollCountRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const notifyChanged = useCallback(async () => {
    if (!onChanged) return;

    try {
      await onChanged();
    } catch (err) {
      // A devolução já foi gravada; falha ao recarregar a venda não é erro dela.
    }
  }, [onChanged]);

  const refresh = useCallback(async () => {
    if (!saleId) return null;

    try {
      const { data: resp } = await api.get(`/correios/reversa/${saleId}`);
      const payload = resp?.data || null;

      if (mountedRef.current) setData(payload);

      return payload;
    } catch (err) {
      if (err?.response?.status === 404 && mountedRef.current) setData(null);

      return null;
    }
  }, [saleId]);

  // Carrega a devolução quando a venda tem (ou passa a ter) devolução pelos Correios
  useEffect(() => {
    if (hasReverse) {
      refresh();
    } else {
      setData(null);
    }
  }, [hasReverse, returnDeliveryId, returnDeliveryStatus, refresh]);

  const labelStatus = data?.label?.status;
  const isLabelProcessing =
    isCorreiosReverseActive(data) &&
    (labelStatus === 'pending' || labelStatus === 'processing');

  // Polling enquanto o rótulo está sendo gerado
  useEffect(() => {
    if (!isLabelProcessing) return undefined;

    pollCountRef.current = 0;

    const interval = setInterval(async () => {
      pollCountRef.current += 1;

      if (pollCountRef.current > MAX_LABEL_POLLS) {
        clearInterval(interval);

        if (mountedRef.current) {
          setData(prev =>
            prev
              ? {
                  ...prev,
                  label: {
                    ...prev.label,
                    status: 'failed',
                    last_error:
                      'Tempo limite excedido ao gerar a etiqueta. Tente novamente.',
                  },
                }
              : prev
          );
        }

        return;
      }

      const payload = await refresh();

      if (payload?.label?.status === 'ready') {
        toast.success('Etiqueta de devolução gerada com sucesso!');
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isLabelProcessing, refresh]);

  const create = useCallback(async () => {
    if (!saleId) return;

    try {
      setIsCreating(true);

      const { data: resp } = await api.post('/correios/reversa', {
        cart_id: saleId,
      });

      setData(resp?.data || null);
      toast.success('Devolução registrada nos Correios! Gerando etiqueta...');

      await notifyChanged();
    } catch (err) {
      toast.error(
        getErrorMessage(err, 'Não foi possível gerar a etiqueta de devolução.'),
        { autoClose: 8000 }
      );
    } finally {
      if (mountedRef.current) setIsCreating(false);
    }
  }, [saleId, notifyChanged]);

  const retryLabel = useCallback(async () => {
    if (!saleId) return;

    try {
      setIsRetrying(true);

      const { data: resp } = await api.post(
        `/correios/reversa/${saleId}/rotulo/retry`
      );

      setData(resp?.data || null);
      toast.info('Etiqueta re-enfileirada. Aguarde...');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Falha ao tentar novamente.'));
    } finally {
      if (mountedRef.current) setIsRetrying(false);
    }
  }, [saleId]);

  const downloadLabel = useCallback(async () => {
    if (!saleId) return;

    try {
      setIsDownloading(true);

      const response = await api.get(
        `/correios/reversa/${saleId}/rotulo/download`,
        { responseType: 'blob' }
      );

      // 202 = rótulo ainda em processamento (o corpo não é o PDF)
      if (response.status === 202) {
        toast.info('A etiqueta ainda está sendo gerada. Aguarde...');
        refresh();
        return;
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `etiqueta-devolucao-${orderNumber || saleId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Erro ao baixar a etiqueta de devolução.');
    } finally {
      if (mountedRef.current) setIsDownloading(false);
    }
  }, [saleId, orderNumber, refresh]);

  const cancel = useCallback(async () => {
    if (!saleId) return;

    try {
      setIsCancelling(true);

      const { data: resp } = await api.delete(`/correios/reversa/${saleId}`);

      setData(resp?.data || null);
      toast.success('Devolução cancelada.');

      await notifyChanged();
    } catch (err) {
      toast.error(
        getErrorMessage(err, 'Não foi possível cancelar a devolução.'),
        { autoClose: 8000 }
      );
    } finally {
      if (mountedRef.current) setIsCancelling(false);
    }
  }, [saleId, notifyChanged]);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  return {
    sale,
    data,
    isActive: isCorreiosReverseActive(data),
    isLabelProcessing,
    isModalOpen,
    isCreating,
    isRetrying,
    isCancelling,
    isDownloading,
    openModal,
    closeModal,
    refresh,
    create,
    retryLabel,
    downloadLabel,
    cancel,
  };
}

const getServiceLabel = data =>
  data?.service_type ? `${data.service_type} REVERSO` : '-';

const getStatusLabel = data =>
  STATUS_LABELS[data?.status] || data?.status || '-';

const formatPackage = pkg => {
  if (!pkg) return null;

  const dims = [pkg.comprimento, pkg.largura, pkg.altura].every(
    v => v !== null && v !== undefined && v !== ''
  )
    ? `${pkg.comprimento} x ${pkg.largura} x ${pkg.altura} cm`
    : null;
  const weight =
    pkg.peso !== null && pkg.peso !== undefined && pkg.peso !== ''
      ? `${pkg.peso} kg`
      : null;

  return {
    name: pkg.box_name || 'Não identificado!',
    details: [dims, weight].filter(Boolean).join(' • '),
  };
};

const Field = ({ label, children }) => (
  <Col md={6} className="px-0 mb-2 mb-lg-0">
    <div className="font-size-14 font-weight-600 text-gray-600">{label}</div>
    <div className="font-size-14 text-gray-700">{children}</div>
  </Col>
);

const TrackingCodeBox = ({ code, variant }) => {
  if (!code) return null;

  const isSuccess = variant === 'success';

  return (
    <div
      className={`mx-auto py-2 px-3 d-inline-block ${
        isSuccess ? 'text-center mb-3 w-100' : 'mt-3'
      }`}
      style={
        isSuccess
          ? {
              border: '1px solid #34c38f',
              borderRadius: '6px',
              backgroundColor: 'rgba(52, 195, 143, 0.08)',
            }
          : { border: '1px dashed #adb5bd', borderRadius: '6px' }
      }
    >
      <small
        className="text-muted d-block"
        style={{ fontSize: '11px', letterSpacing: '0.5px' }}
      >
        CÓDIGO DE RASTREIO
      </small>
      <span
        className={`font-size-16 font-weight-600 ${
          variant === 'processing' ? 'text-primary' : ''
        }`}
        style={isSuccess ? { color: '#34c38f' } : undefined}
      >
        {code}
      </span>
    </div>
  );
};

/**
 * Modal no mesmo padrão do modal "Pré Postagem e Gerar Etiqueta" da ida:
 * confirmação -> criando -> gerando etiqueta -> pronta / falha.
 */
export function CorreiosReverseModal({ reverse, sale }) {
  const [confirmCancel, setConfirmCancel] = useState(false);

  const { data, isActive, isModalOpen, closeModal, isCreating } = reverse;
  const labelStatus = data?.label?.status;

  const isIdle = !isActive && !isCreating;
  const isProcessing = isActive && !isCreating && reverse.isLabelProcessing;
  const isFailed = isActive && !isCreating && labelStatus === 'failed';
  const isReady = isActive && !isCreating && !isProcessing && !isFailed;
  const canCancel = isActive && !isCreating && data?.status === SEARCHING;

  useEffect(() => {
    if (!isModalOpen) setConfirmCancel(false);
  }, [isModalOpen]);

  const handleCancel = async () => {
    await reverse.cancel();
    setConfirmCancel(false);
  };

  return (
    <Modal isOpen={isModalOpen} toggle={closeModal}>
      <ModalHeader toggle={closeModal}>
        Devolução e Etiqueta Reversa
      </ModalHeader>

      <ModalBody>
        {/* ─── ESTADO: IDLE ─── */}
        {isIdle && (
          <>
            <p className="mb-3">
              Ao confirmar, vamos registrar a{' '}
              <strong>pré-postagem de devolução</strong> (logística reversa) do
              pedido <strong>#{sale?.order_number}</strong> nos Correios e gerar
              a <strong>etiqueta de devolução</strong> em PDF.
            </p>

            <p className="mb-2 font-weight-600">O que vai acontecer agora:</p>
            <ul className="pl-3 mb-3">
              <li>
                O sistema vai gerar a etiqueta com o cliente{' '}
                <strong>{sale?.client?.name || ''}</strong> como remetente e a
                loja <strong>{sale?.store?.fantasy_name || ''}</strong> como
                destinatário;
              </li>
              <li>Você deverá baixar a etiqueta e enviá-la ao cliente;</li>
              <li>
                O cliente cola a etiqueta no pacote e posta em uma agência dos
                Correios.
              </li>
            </ul>

            <p className="mb-2 font-weight-600">Importante:</p>
            <ul className="pl-3 mb-0">
              <li>
                A etiqueta é válida por <strong>30 dias</strong>. Depois disso,
                a devolução é cancelada e será preciso gerar outra.
              </li>
              <li>
                O frete da devolução é faturado no contrato dos Correios quando
                o objeto for postado.
              </li>
              <li>
                Quando os Correios registrarem a entrega na loja, o pedido passa
                para <strong>Pedido devolvido</strong>.
              </li>
            </ul>
          </>
        )}

        {/* ─── ESTADO: CRIANDO PRÉ-POSTAGEM ─── */}
        {isCreating && (
          <div className="text-center py-4">
            <i className="fas fa-spinner fa-spin font-size-24 text-primary mb-3 d-block" />
            <p className="font-size-16 font-weight-600 mb-1">
              Criando pré-postagem de devolução...
            </p>
            <p className="text-muted font-size-14 mb-0">
              Registrando a devolução nos Correios. Aguarde alguns segundos.
            </p>
          </div>
        )}

        {/* ─── ESTADO: PENDING / PROCESSING ─── */}
        {isProcessing && (
          <div className="text-center py-4">
            <i className="fas fa-spinner fa-spin font-size-24 text-primary mb-3 d-block" />
            <p className="font-size-16 font-weight-600 mb-1">
              Gerando etiqueta de devolução, aguarde...
            </p>
            <p className="text-muted font-size-14 mb-2">
              A etiqueta está sendo processada pelos Correios. Isso pode levar
              alguns instantes.
            </p>

            <TrackingCodeBox code={data?.tracking_code} variant="processing" />

            <p className="text-muted font-size-12 mt-3 mb-0">
              Você pode fechar este modal. A etiqueta continuará sendo
              processada.
            </p>
          </div>
        )}

        {/* ─── ESTADO: READY ─── */}
        {isReady && (
          <>
            <div className="text-center mb-3">
              <i
                className="fas fa-check-circle font-size-36 mb-2 d-block"
                style={{ color: '#34c38f' }}
              />
              <p className="font-size-16 font-weight-600 mb-1">
                Etiqueta de devolução gerada com sucesso!
              </p>
            </div>

            <TrackingCodeBox code={data?.tracking_code} variant="success" />

            <Row className="w-100 mx-0 mb-3">
              <Field label="Serviço">{getServiceLabel(data)}</Field>
              <Field label="Postar até">{formatDate(data?.valid_until)}</Field>
            </Row>

            {data?.status === SEARCHING ? (
              <>
                <p className="mb-2 font-weight-600">Próximos passos:</p>
                <ul className="pl-3 mb-0">
                  <li>Baixe o PDF e envie a etiqueta ao cliente.</li>
                  <li>
                    O cliente imprime, cola a etiqueta na parte externa do
                    pacote e posta em uma agência dos Correios até{' '}
                    <strong>{formatDate(data?.valid_until)}</strong>.
                  </li>
                  <li>
                    Acompanhe o rastreio no card <strong>Devolução</strong> do
                    pedido.
                  </li>
                </ul>
              </>
            ) : (
              <p className="mb-0">
                <span className="font-weight-600">Status da devolução:</span>{' '}
                {getStatusLabel(data)}
              </p>
            )}
          </>
        )}

        {/* ─── ESTADO: FAILED ─── */}
        {isFailed && (
          <div className="text-center py-3">
            <i className="fas fa-exclamation-triangle font-size-36 text-danger mb-2 d-block" />
            <p className="font-size-16 font-weight-600 mb-2">
              Não foi possível gerar a etiqueta de devolução
            </p>
            <p className="text-muted font-size-14 mb-3">
              {data?.label?.last_error ||
                'Ocorreu um erro ao processar o rótulo nos Correios. Tente novamente.'}
            </p>

            <TrackingCodeBox code={data?.tracking_code} />
          </div>
        )}

        {canCancel && confirmCancel && (
          <p className="text-danger font-size-13 mt-3 mb-0">
            Confirma o cancelamento desta devolução? A etiqueta deixa de valer
            nos Correios.
          </p>
        )}
      </ModalBody>

      <ModalFooter>
        {isIdle && (
          <>
            <Button
              color="secondary"
              className="new-layout-btn main"
              onClick={closeModal}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              className="new-layout-btn main"
              onClick={reverse.create}
            >
              Gerar Etiqueta de Devolução
            </Button>
          </>
        )}

        {canCancel && !confirmCancel && (
          <Button
            color="danger"
            outline
            className="new-layout-btn main mr-auto"
            onClick={() => setConfirmCancel(true)}
          >
            Cancelar devolução
          </Button>
        )}

        {canCancel && confirmCancel && (
          <Button
            color="danger"
            className="new-layout-btn main mr-auto"
            onClick={handleCancel}
            disabled={reverse.isCancelling}
          >
            {reverse.isCancelling ? 'Cancelando...' : 'Confirmar cancelamento'}
          </Button>
        )}

        {!isIdle && (
          <Button
            color="secondary"
            className="new-layout-btn main"
            onClick={closeModal}
          >
            Fechar
          </Button>
        )}

        {isReady && (
          <Button
            color="primary"
            className="new-layout-btn main"
            onClick={reverse.downloadLabel}
            disabled={reverse.isDownloading}
          >
            <i
              className={`fas ${
                reverse.isDownloading ? 'fa-spinner fa-spin' : 'fa-download'
              } mr-2`}
            />
            Baixar Etiqueta (PDF)
          </Button>
        )}

        {isFailed && (
          <Button
            color="primary"
            className="new-layout-btn main"
            onClick={reverse.retryLabel}
            disabled={reverse.isRetrying}
          >
            {reverse.isRetrying ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2" />
                Reenviando...
              </>
            ) : (
              <>
                <i className="fas fa-redo mr-2" />
                Tentar Novamente
              </>
            )}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

const ReverseLabelActions = ({ reverse }) => {
  const { data } = reverse;

  if (!isCorreiosReverseActive(data)) return null;

  if (reverse.isLabelProcessing) {
    return (
      <div className="text-muted font-size-13">
        <i className="fas fa-spinner fa-spin mr-2" />A etiqueta está sendo
        processada pelos Correios. Isso pode levar alguns instantes.
      </div>
    );
  }

  if (data?.label?.status === 'failed') {
    return (
      <>
        <div className="text-danger font-size-13 mb-2">
          {data?.label?.last_error ||
            'Ocorreu um erro ao processar o rótulo nos Correios.'}
        </div>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={reverse.retryLabel}
          disabled={reverse.isRetrying}
        >
          <i
            className={`fas ${
              reverse.isRetrying ? 'fa-spinner fa-spin' : 'fa-redo'
            } mr-2`}
          />
          Tentar novamente
        </button>
      </>
    );
  }

  return (
    <button
      type="button"
      className="btn btn-outline-primary"
      onClick={reverse.downloadLabel}
      disabled={reverse.isDownloading}
    >
      <i
        className={`fas ${
          reverse.isDownloading ? 'fa-spinner fa-spin' : 'fa-download'
        } mr-2`}
      />
      Baixar etiqueta de devolução
    </button>
  );
};

/**
 * Card no mesmo padrão do card "Entrega" dos Correios (CorreiosInfoCard).
 */
export function CorreiosReverseInfoCard({ reverse }) {
  const [expanded, setExpanded] = useState(
    typeof window !== 'undefined' ? window.innerWidth > 768 : true
  );
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);

  const data = reverse?.data;
  const cart = reverse?.sale;

  if (!data) return null;

  const pkg = formatPackage(data.package);

  const events = Array.isArray(data?.tracking?.events)
    ? data.tracking.events
        .slice()
        .sort(
          (a, b) =>
            new Date(b.created_at || 0).getTime() -
            new Date(a.created_at || 0).getTime()
        )
    : [];

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <Row
          className="w-100 mx-0 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <Col className="font-size-16 font-weight-600 px-0">
            <Row className="w-100 mx-0 d-flex justify-content-between align-items-center">
              <span className="px-0">Devolução</span>
              <IconButton
                icon={`las la-${expanded ? 'angle-up' : 'angle-right'}`}
                click={() => setExpanded(!expanded)}
                color="gray-700"
              />
            </Row>
          </Col>
        </Row>

        <Collapse isOpen={expanded}>
          <Row className="w-100 mx-0 pt-3">
            <Field label="Endereço de coleta">
              <span className="font-size-13">
                {cart?.delivery_address?.full_address || '-'}
              </span>
            </Field>
            <Field label="Endereço de entrega">
              <span className="font-size-13">
                {cart?.store?.fantasy_name || '-'}
              </span>
            </Field>
          </Row>

          <Row className="w-100 mx-0 pt-lg-3">
            <Field label="Transportadora">Correios</Field>
            <Field label="Método">{getServiceLabel(data)}</Field>
          </Row>

          <Row className="w-100 mx-0 pt-lg-3">
            <Field label="Pacote">
              {pkg ? (
                <>
                  {pkg.name}
                  {pkg.details && (
                    <div className="text-muted font-size-12">{pkg.details}</div>
                  )}
                </>
              ) : (
                '-'
              )}
            </Field>
            <Field label="Postar até">{formatDate(data.valid_until)}</Field>
          </Row>

          <hr />

          <Row className="w-100 mx-0 mt-2">
            <Field label="Código de rastreio">
              {data.tracking_code || '-'}
            </Field>
            <Field label="Status">{getStatusLabel(data)}</Field>
          </Row>

          {data?.tracking?.current_status && (
            <Row className="w-100 mx-0 pt-lg-3">
              <Col className="px-0 mb-2 mb-lg-0">
                <div className="font-size-14 font-weight-600 text-gray-600">
                  Último evento
                </div>
                <div className="font-size-14 text-gray-700">
                  {data.tracking.current_status}
                  {data.tracking.current_event_at
                    ? ` (${formatDateTime(data.tracking.current_event_at)})`
                    : ''}
                </div>
              </Col>
            </Row>
          )}

          {data.status === CANCELED && data.cancellation_reason && (
            <Row className="w-100 mx-0 pt-3">
              <Col className="px-0 text-danger font-size-13">
                {data.cancellation_reason}
              </Col>
            </Row>
          )}

          {events.length === 0 && data.status === SEARCHING && (
            <Row className="w-100 mx-0 pt-3">
              <Col className="px-0">
                <div className="text-muted font-size-13">
                  O histórico de eventos aparece após o cliente postar o objeto
                  em uma agência dos Correios.
                </div>
              </Col>
            </Row>
          )}

          <Row className="w-100 mx-0 pt-3 align-items-center">
            <Col className="px-0">
              <ReverseLabelActions reverse={reverse} />
            </Col>
            {events.length > 0 && (
              <Col xs="auto" className="px-0 pl-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setIsTimelineOpen(true)}
                >
                  Ver histórico
                </button>
              </Col>
            )}
          </Row>

          {/* Mesmo modal de histórico do card "Entrega" (CorreiosInfoCard) */}
          {isTimelineOpen && (
            <div
              className="correios-timeline-backdrop px-3"
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.35)',
                zIndex: 1050,
              }}
            >
              <div
                className="correios-timeline-modal"
                style={{
                  maxWidth: 600,
                  width: '100%',
                  margin: '40px auto',
                  background: '#fff',
                  borderRadius: 8,
                  padding: 20,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Histórico de Rastreamento da Devolução
                  </h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => setIsTimelineOpen(false)}
                  >
                    Fechar
                  </button>
                </div>

                <p className="mb-3">
                  <strong>Código:</strong> {data.tracking_code || '-'}
                </p>

                <ul className="list-unstyled mt-3 mb-0">
                  {events.map((event, index) => (
                    <li
                      key={`${event.created_at || ''}-${event.code || ''}-${
                        event.description || ''
                      }`}
                      className="mb-3 d-flex"
                    >
                      <div
                        style={{
                          width: 10,
                          marginRight: 10,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            marginTop: 6,
                            backgroundColor:
                              index === 0 ? '#0d6efd' : '#ced4da',
                          }}
                        />
                      </div>
                      <div>
                        <div className="small text-muted">
                          {formatDateTime(event.created_at)}
                        </div>
                        <div className="fw-semibold">
                          {event.description || '-'}
                        </div>
                        {(event.from_city || event.to_city) && (
                          <div className="text-muted small">
                            {event.from_city && event.from_state && (
                              <>
                                {event.to_city ? (
                                  <>
                                    de {event.from_type}, {event.from_city} -{' '}
                                    {event.from_state}
                                  </>
                                ) : (
                                  <>
                                    {event.from_type}, {event.from_city} -{' '}
                                    {event.from_state}
                                  </>
                                )}
                                {event.to_city && <br />}
                              </>
                            )}
                            {event.to_type && event.to_city && event.to_state && (
                              <>
                                para {event.to_type}, {event.to_city} -{' '}
                                {event.to_state}
                              </>
                            )}
                          </div>
                        )}
                        {event.details && (
                          <div className="text-muted small">
                            {event.details}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </Collapse>
      </CardBody>
    </Card>
  );
}
